import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/queryClient'
import { DEFAULT_SERVICE_REQUEST_STATUSES } from '@shared/constants/service-request-statuses'

export type NotificationCondition = {
	id: string
	type: 'email' | 'popup' | 'both'
	timeAmount: number
	timeUnit: 'minutes' | 'hours' | 'days'
	beforeEvent: boolean
	enabled: boolean
	description?: string
}

export type NotificationSettings = {
	conditions: NotificationCondition[]
	fromEmail?: string
	emailTemplate?: string
	showDashboardNotifications?: boolean
	popupDuration?: number
	persistUntilClosed?: boolean
}

export type AddressInfo = {
	street: string
	street2?: string
	city: string
	state: string
	zipCode: string
}

export type LogoInfo = {
	url: string
	filename: string
	originalFilename: string
	fileSize: number
	timestamp: string
}

export type ShopConfig = {
	id: 'shop_config'
	name: string
	addressInfo: AddressInfo
	phone: string
	email: string
	website?: string
	defaultShopRate: number
	taxRate: number // 0..1
	shopHours?: string
	currency?: string
	logo: LogoInfo | null
	notificationSettings?: NotificationSettings
	contactFormEmail?: string
	createdAt?: string
	updatedAt?: string
	serviceTypes?: { id?: string; value: string; label: string }[]
	serviceCategories?: { id?: string; value: string; label: string }[]
	serviceRequestStatuses?: { id?: string; value: string; label: string; color?: string; isDefault?: boolean }[]
}

const defaultConfig: ShopConfig = {
	id: 'shop_config',
	name: '',
	addressInfo: { street: '', city: '', state: '', zipCode: '' },
	phone: '',
	email: '',
	website: '',
	defaultShopRate: 150,
	taxRate: 0,
	shopHours: '',
	currency: 'USD',
	logo: null,
	notificationSettings: {
		conditions: [],
		showDashboardNotifications: true,
		popupDuration: 5000,
		persistUntilClosed: false,
		emailTemplate: 'Dear {customer},...'
	},
	contactFormEmail: '',
	serviceTypes: [],
	serviceCategories: [],
	serviceRequestStatuses: DEFAULT_SERVICE_REQUEST_STATUSES,
}

function normalizeConfig(value: unknown): ShopConfig {
	const base = typeof value === 'object' && value ? (value as Partial<ShopConfig>) : {}
	return { ...defaultConfig, ...base, id: 'shop_config' }
}

export function useShopConfig() {
	const qc = useQueryClient()
	const query = useQuery({
		queryKey: ['shopConfig'],
		queryFn: async () => {
			const res = await apiRequest('GET', '/api/v1/settings')
			const json = await res.json()
			const entry = (json.settings as Array<{ key: string; value: unknown }>).find((r) => r.key === 'shop.config')
			return normalizeConfig(entry?.value)
		},
	})

	const mutation = useMutation({
		mutationFn: async (updates: Partial<ShopConfig>) => {
			const current = query.data ?? defaultConfig
			const payload: ShopConfig = { ...current, ...updates, id: 'shop_config', updatedAt: new Date().toISOString() }
			await apiRequest('POST', '/api/v1/settings', { key: 'shop.config', value: payload })
			return payload
		},
		onSuccess: (data) => {
			qc.setQueryData(['shopConfig'], data)
		},
	})

	return {
		config: query.data,
		isLoading: query.isLoading,
		updateConfig: mutation.mutateAsync,
	}
}



